import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Tab,
  Tabs,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fade,
  Slide,
  IconButton,
  InputAdornment,
  Chip,
  Stack,
  Card,
  CardContent,
  Grid,
  useTheme,
  alpha,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  PersonAdd,
  Login as LoginIcon,
  AdminPanelSettings,
  Person,
  Security,
  MedicalServices,
  LocalHospital,
  Psychology,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';

interface LoginFormData {
  username: string;
  password: string;
}

interface RegisterFormData {
  username: string;
  password: string;
  confirmPassword: string;
  role: string;
}

const loginSchema = yup.object({
  username: yup.string().required('Username is required'),
  password: yup.string().required('Password is required'),
});

const registerSchema = yup.object({
  username: yup.string().required('Username is required').min(3, 'Username must be at least 3 characters'),
  password: yup.string().required('Password is required').min(6, 'Password must be at least 6 characters'),
  confirmPassword: yup.string()
    .required('Please confirm your password')
    .oneOf([yup.ref('password')], 'Passwords must match'),
  role: yup.string().required('Please select a role'),
});

const LoginPage: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const { login, register } = useAuth();
  const theme = useTheme();

  const loginForm = useForm<LoginFormData>({
    resolver: yupResolver(loginSchema),
    defaultValues: {
      username: '',
      password: '',
    },
  });

  const registerForm = useForm<RegisterFormData>({
    resolver: yupResolver(registerSchema),
    defaultValues: {
      username: '',
      password: '',
      confirmPassword: '',
      role: 'user',
    },
  });

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setError(null);
    setSuccess(null);
    loginForm.reset();
    registerForm.reset();
  };

  const onLoginSubmit = async (data: LoginFormData) => {
    setIsLoading(true);
    setError(null);
    
    try {
      await login(data.username, data.password);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setIsLoading(false);
    }
  };

  const onRegisterSubmit = async (data: RegisterFormData) => {
    setIsLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await register(data.username, data.password, data.role);
      setSuccess('Registration successful! You can now log in.');
      setTabValue(0); // Switch to login tab
      registerForm.reset();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4,
      }}
    >
      <Container component="main" maxWidth="lg">
        <Grid container spacing={4} alignItems="center" justifyContent="center">
          {/* Left side - Branding */}
          <Grid item xs={12} md={6}>
            <Slide direction="right" in={true} timeout={800}>
              <Box sx={{ textAlign: { xs: 'center', md: 'left' }, mb: { xs: 4, md: 0 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', md: 'flex-start' }, mb: 3 }}>
                  <MedicalServices sx={{ fontSize: 60, color: theme.palette.primary.main, mr: 2 }} />
                  <Typography
                    variant="h2"
                    component="h1"
                    sx={{
                      fontWeight: 800,
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                      backgroundClip: 'text',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                    }}
                  >
                    MELIA
                  </Typography>
                </Box>

                <Typography variant="h4" color="text.primary" gutterBottom sx={{ fontWeight: 600 }}>
                  Advanced Melanoma Detection System
                </Typography>

                <Typography variant="h6" color="text.secondary" paragraph>
                  AI-powered preliminary skin lesion evaluation for preventive healthcare
                </Typography>

                <Stack direction="row" spacing={2} sx={{ mt: 4, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                  <Chip
                    icon={<Psychology />}
                    label="AI-Powered Analysis"
                    variant="outlined"
                    color="primary"
                    sx={{ fontSize: '0.9rem', py: 2 }}
                  />
                  <Chip
                    icon={<Security />}
                    label="Secure & Private"
                    variant="outlined"
                    color="secondary"
                    sx={{ fontSize: '0.9rem', py: 2 }}
                  />
                  <Chip
                    icon={<LocalHospital />}
                    label="Medical Grade"
                    variant="outlined"
                    color="primary"
                    sx={{ fontSize: '0.9rem', py: 2 }}
                  />
                </Stack>
              </Box>
            </Slide>
          </Grid>

          {/* Right side - Authentication */}
          <Grid item xs={12} md={6}>
            <Slide direction="left" in={true} timeout={800}>
              <Card
                elevation={0}
                sx={{
                  maxWidth: 500,
                  mx: 'auto',
                  background: 'rgba(255, 255, 255, 0.95)',
                  backdropFilter: 'blur(20px)',
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  borderRadius: 4,
                  overflow: 'visible',
                  position: 'relative',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: 4,
                    background: `linear-gradient(90deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    borderRadius: '16px 16px 0 0',
                  },
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Typography variant="h4" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
                      Welcome Back
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                      Access your MELIA dashboard
                    </Typography>
                  </Box>

                  <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                    <Tabs
                      value={tabValue}
                      onChange={handleTabChange}
                      centered
                      sx={{
                        '& .MuiTab-root': {
                          minHeight: 60,
                          fontSize: '1rem',
                          fontWeight: 600,
                          textTransform: 'none',
                        },
                        '& .Mui-selected': {
                          color: theme.palette.primary.main,
                        },
                      }}
                    >
                      <Tab
                        icon={<LoginIcon />}
                        iconPosition="start"
                        label="Sign In"
                      />
                      <Tab
                        icon={<PersonAdd />}
                        iconPosition="start"
                        label="Register"
                      />
                    </Tabs>
                  </Box>

                  {error && (
                    <Fade in={!!error}>
                      <Alert
                        severity="error"
                        sx={{
                          mb: 3,
                          borderRadius: 2,
                          '& .MuiAlert-icon': {
                            fontSize: '1.5rem',
                          },
                        }}
                      >
                        {error}
                      </Alert>
                    </Fade>
                  )}

                  {success && (
                    <Fade in={!!success}>
                      <Alert
                        severity="success"
                        sx={{
                          mb: 3,
                          borderRadius: 2,
                          '& .MuiAlert-icon': {
                            fontSize: '1.5rem',
                          },
                        }}
                      >
                        {success}
                      </Alert>
                    </Fade>
                  )}

                  {/* Login Form */}
                  {tabValue === 0 && (
                    <Fade in={tabValue === 0} timeout={500}>
                      <Box component="form" onSubmit={loginForm.handleSubmit(onLoginSubmit)}>
                        <TextField
                          margin="normal"
                          required
                          fullWidth
                          id="login-username"
                          label="Username"
                          autoComplete="username"
                          autoFocus
                          {...loginForm.register('username')}
                          error={!!loginForm.formState.errors.username}
                          helperText={loginForm.formState.errors.username?.message}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Person color="action" />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                              },
                            },
                          }}
                        />
                        <TextField
                          margin="normal"
                          required
                          fullWidth
                          label="Password"
                          type={showPassword ? 'text' : 'password'}
                          id="login-password"
                          autoComplete="current-password"
                          {...loginForm.register('password')}
                          error={!!loginForm.formState.errors.password}
                          helperText={loginForm.formState.errors.password?.message}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Security color="action" />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={() => setShowPassword(!showPassword)}
                                  edge="end"
                                >
                                  {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                              },
                            },
                          }}
                        />
                        <Button
                          type="submit"
                          fullWidth
                          variant="contained"
                          size="large"
                          disabled={isLoading}
                          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <LoginIcon />}
                          sx={{
                            mt: 4,
                            mb: 2,
                            py: 1.5,
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            borderRadius: 2,
                            textTransform: 'none',
                            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                            '&:hover': {
                              background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                              transform: 'translateY(-2px)',
                              boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`,
                            },
                            '&:disabled': {
                              background: alpha(theme.palette.action.disabled, 0.12),
                            },
                          }}
                        >
                          {isLoading ? 'Signing In...' : 'Sign In to MELIA'}
                        </Button>
                      </Box>
                    </Fade>
                  )}

                  {/* Register Form */}
                  {tabValue === 1 && (
                    <Fade in={tabValue === 1} timeout={500}>
                      <Box component="form" onSubmit={registerForm.handleSubmit(onRegisterSubmit)}>
                        <TextField
                          margin="normal"
                          required
                          fullWidth
                          id="register-username"
                          label="Username"
                          autoComplete="username"
                          autoFocus
                          {...registerForm.register('username')}
                          error={!!registerForm.formState.errors.username}
                          helperText={registerForm.formState.errors.username?.message}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Person color="action" />
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                              },
                            },
                          }}
                        />

                        <FormControl
                          fullWidth
                          margin="normal"
                          error={!!registerForm.formState.errors.role}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                              },
                            },
                          }}
                        >
                          <InputLabel id="role-select-label">Account Type</InputLabel>
                          <Select
                            labelId="role-select-label"
                            id="role-select"
                            label="Account Type"
                            {...registerForm.register('role')}
                            defaultValue="user"
                            startAdornment={
                              <InputAdornment position="start">
                                {registerForm.watch('role') === 'admin' ? (
                                  <AdminPanelSettings color="action" />
                                ) : (
                                  <Person color="action" />
                                )}
                              </InputAdornment>
                            }
                          >
                            <MenuItem value="user">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Person />
                                <Box>
                                  <Typography variant="body1">Standard User</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Upload and analyze skin lesions
                                  </Typography>
                                </Box>
                              </Box>
                            </MenuItem>
                            <MenuItem value="admin">
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AdminPanelSettings />
                                <Box>
                                  <Typography variant="body1">Administrator</Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Full system access and analytics
                                  </Typography>
                                </Box>
                              </Box>
                            </MenuItem>
                          </Select>
                          {registerForm.formState.errors.role && (
                            <Typography variant="caption" color="error" sx={{ mt: 1, ml: 2 }}>
                              {registerForm.formState.errors.role.message}
                            </Typography>
                          )}
                        </FormControl>

                        <TextField
                          margin="normal"
                          required
                          fullWidth
                          label="Password"
                          type={showPassword ? 'text' : 'password'}
                          id="register-password"
                          autoComplete="new-password"
                          {...registerForm.register('password')}
                          error={!!registerForm.formState.errors.password}
                          helperText={registerForm.formState.errors.password?.message}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Security color="action" />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={() => setShowPassword(!showPassword)}
                                  edge="end"
                                >
                                  {showPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                              },
                            },
                          }}
                        />
                        <TextField
                          margin="normal"
                          required
                          fullWidth
                          label="Confirm Password"
                          type={showConfirmPassword ? 'text' : 'password'}
                          id="register-confirm-password"
                          autoComplete="new-password"
                          {...registerForm.register('confirmPassword')}
                          error={!!registerForm.formState.errors.confirmPassword}
                          helperText={registerForm.formState.errors.confirmPassword?.message}
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <Security color="action" />
                              </InputAdornment>
                            ),
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle confirm password visibility"
                                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                  edge="end"
                                >
                                  {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                transform: 'translateY(-1px)',
                                boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                              },
                            },
                          }}
                        />
                        <Button
                          type="submit"
                          fullWidth
                          variant="contained"
                          size="large"
                          disabled={isLoading}
                          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <PersonAdd />}
                          sx={{
                            mt: 4,
                            mb: 2,
                            py: 1.5,
                            fontSize: '1.1rem',
                            fontWeight: 600,
                            borderRadius: 2,
                            textTransform: 'none',
                            background: `linear-gradient(135deg, ${theme.palette.secondary.main} 0%, ${theme.palette.error.main} 100%)`,
                            '&:hover': {
                              background: `linear-gradient(135deg, ${theme.palette.secondary.dark} 0%, ${theme.palette.error.dark} 100%)`,
                              transform: 'translateY(-2px)',
                              boxShadow: `0 8px 24px ${alpha(theme.palette.secondary.main, 0.4)}`,
                            },
                            '&:disabled': {
                              background: alpha(theme.palette.action.disabled, 0.12),
                            },
                          }}
                        >
                          {isLoading ? 'Creating Account...' : 'Create MELIA Account'}
                        </Button>
                      </Box>
                    </Fade>
                  )}

                  <Box sx={{ mt: 4, pt: 3, borderTop: `1px solid ${alpha(theme.palette.divider, 0.1)}` }}>
                    <Alert
                      severity="info"
                      sx={{
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.info.main, 0.05),
                        border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                        '& .MuiAlert-icon': {
                          color: theme.palette.info.main,
                        },
                      }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        <strong>Medical Disclaimer:</strong> MELIA provides AI-assisted preliminary evaluation only.
                        This tool does NOT constitute medical diagnosis. Always consult healthcare professionals
                        for proper skin health monitoring and medical advice.
                      </Typography>
                    </Alert>
                  </Box>
                </CardContent>
              </Card>
            </Slide>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default LoginPage;
